import {Injectable} from '@angular/core';
import {Mapper} from '../../../../../core/abstracts/mapper.abstract';
import {NewVehicleFormPayload} from '../model/new-vehicle-form.model';
import {NewVehicleRequest} from '../model/new-vehicle-request.model';
import {Brand} from '../../../brand/models/brand.model';
import {PropertyRequest} from '../../../property/model/property-request.model';
import {AvailablePropertyType} from '../../../property/enum/available-property.type';
import {VehicleModel} from '../../../model/model/vehicle-model.model';
import {DateTime} from 'luxon';
import {toIsoDateTimeHelper} from '../../../../../core/helpers/to-iso-date-time.helper';
import {toIsoDateHelper} from '../../../../../core/helpers/to-iso-date.helper';

@Injectable({providedIn: 'root'})
export class NewVehicleRequestMapper extends Mapper<NewVehicleFormPayload, NewVehicleRequest> {
  override map(source: NewVehicleFormPayload): NewVehicleRequest {
    const properties: PropertyRequest[] = [];

    this.resolveBrandProperty(source.vehicleSpecificationSection.brand, properties);
    this.resolveModelProperty(source.vehicleSpecificationSection.model, properties);
    this.resolveRegistrationPlateProperty(source.vehicleIdentificationSection.registrationPlate, properties);
    this.resolveVinProperty(source.vehicleIdentificationSection.vin, properties);
    this.resolveNumberAndSeriesOfRegistrationCardProperty(source.vehicleIdentificationSection.numberAndSeriesOfRegistrationCard, properties);
    this.resolveTechnicalInspectionDateProperty(source.vehicleServiceSection.technicalInspectionTerminationDate, properties);
    this.resolveTachographInspectionDate(source.vehicleServiceSection.tachographInspectionDate, properties);
    this.resolveInsuranceExpiryDateProperty(source.vehicleInsuranceSection.insuranceTerminationDate, properties);
    this.resolveIsInsurancePolicyProperty(source.vehicleInsuranceSection.hasInsurance, properties);
    this.resolveUFGInsuranceExpiryDateProperty(source.vehicleInsuranceSection.ufgVerifiedTerminationDate, properties);
    this.resolveIdentificationNumberProperty(source.vehicleIdentificationSection.identificationNumber, properties);
    this.resolveFactoryNumberProperty(source.vehicleIdentificationSection.factoryNumber, properties);
    this.resolveMileageProperty(source.vehicleServiceSection.mileage, properties);
    this.resolveMotoHoursProperty(source.vehicleServiceSection.motoHours, properties);

    return new NewVehicleRequest(
      source.vehicleSpecificationSection.category.category,
      source.vehicleSpecificationSection.owner.id,
      source.vehicleStorageSection.storage.id,
      toIsoDateTimeHelper(source.vehicleSpecificationSection.transferDate),
      source.vehicleSpecificationSection.transferringPerson,
      properties
    );
  }

  private resolveBrandProperty(brand: Brand | null, properties: PropertyRequest[]): void {
    if (!brand) return;

    const property = new PropertyRequest(
      AvailablePropertyType.BRAND,
      brand.name
    );

    properties.push(property);
  }

  private resolveModelProperty(model: VehicleModel | null, properties: PropertyRequest[]): void {
    if (!model) return;

    const property = new PropertyRequest(
      AvailablePropertyType.MODEL,
      model.name
    );

    properties.push(property);
  }

  private resolveRegistrationPlateProperty(registrationPlate: string, properties: PropertyRequest[]): void {
    if (!registrationPlate) return;

    const property = new PropertyRequest(
      AvailablePropertyType.REGISTRATION_PLATE,
      registrationPlate
    );

    properties.push(property);
  }

  private resolveVinProperty(vin: string, properties: PropertyRequest[]): void {
    if (!vin) return;

    const property = new PropertyRequest(
      AvailablePropertyType.VIN,
      vin
    );

    properties.push(property);
  }

  private resolveNumberAndSeriesOfRegistrationCardProperty(value: string, properties: PropertyRequest[]): void {
    if (!value) return;

    const property = new PropertyRequest(
      AvailablePropertyType.NUMBER_AND_SERIES_REGISTRATION_CARD,
      value
    );

    properties.push(property);
  }

  private resolveTechnicalInspectionDateProperty(date: DateTime | null, properties: PropertyRequest[]): void {
    if (!date) return;

    const property = new PropertyRequest(
      AvailablePropertyType.TECHNICAL_INSPECTION_DATE,
      toIsoDateHelper(date)
    );

    properties.push(property);
  }

  private resolveTachographInspectionDate(date: DateTime | null, properties: PropertyRequest[]): void {
    if (!date) return;

    const property = new PropertyRequest(
      AvailablePropertyType.TACHOGRAPH_INSPECTION_DATE,
      toIsoDateHelper(date)
    );

    properties.push(property);
  }

  private resolveInsuranceExpiryDateProperty(date: DateTime | null, properties: PropertyRequest[]): void {
    if (!date) return;

    const property = new PropertyRequest(
      AvailablePropertyType.INSURANCE_EXPIRY_DATE,
      toIsoDateHelper(date)
    );

    properties.push(property);
  }

  private resolveIsInsurancePolicyProperty(isInsurance: boolean | null, properties: PropertyRequest[]): void {
    if (!isInsurance) return;

    const property = new PropertyRequest(
      AvailablePropertyType.INSURANCE_POLICY,
      isInsurance
    );

    properties.push(property);
  }

  private resolveIdentificationNumberProperty(number: string, properties: PropertyRequest[]): void {
    if (!number) return;

    const property = new PropertyRequest(
      AvailablePropertyType.IDENTIFICATION_NUMBER,
      number
    );

    properties.push(property);
  }

  private resolveFactoryNumberProperty(number: string, properties: PropertyRequest[]): void {
    if (!number) return;

    const property = new PropertyRequest(
      AvailablePropertyType.FACTORY_NUMBER,
      number
    );

    properties.push(property);
  }


  private resolveUFGInsuranceExpiryDateProperty(date: DateTime | null, properties: PropertyRequest[]): void {
    if (!date) return;

    const property = new PropertyRequest(
      AvailablePropertyType.UFG,
      toIsoDateHelper(date)
    );

    properties.push(property);
  }

  private resolveMileageProperty(mileage: string | null, properties: PropertyRequest[]): void {
    if (!mileage) return;

    const property = new PropertyRequest(
      AvailablePropertyType.MILEAGE,
      mileage
    );

    properties.push(property);
  }

  private resolveMotoHoursProperty(motoHours: string | null, properties: PropertyRequest[]): void {
    if (!motoHours) return;

    const property = new PropertyRequest(
      AvailablePropertyType.MOTO_HOURS,
      motoHours
    );

    properties.push(property);
  }
}
